import { Grid2, Stack } from '@mui/material';
import PropTypes, { oneOf } from 'prop-types';
import React from 'react';
import { useIntl } from '../../util/reactIntl';
import { CopyLinkButton } from './CopyLinkButton';
import { CopyTextButton } from './CopyTextButton';
import { SHARED_ITEM_NAMES } from './def';
import { EmailButton } from './EmailButton';
import { FacebookButton } from './FacebookButton';
import { MessengerButton } from './MessengerButton';
import css from './ShareButtonGroup.module.css';
import { SMSButton } from './SMSButton';
import { TelegramButton } from './TelegramButton';
import { WhatsAppButton } from './WhatsAppButton';

/**
 * ShareButtonGroup component
 * @param url
 * @param title
 * @param text
 * @param spacing
 * @param sharedItemName name of the item being shared, oneOf SHARED_ITEM_NAMES
 * @param shareOrigin origin of the share, either route name from routeConfiguration e.g.
 * CheckoutPage. If multiple on one page add suffix with component name e.g. CheckoutPage_sharePanel
 * @param className
 * @param onCopied
 * @returns {JSX.Element}
 */
export const ShareButtonGroup = ({
  url,
  title,
  text,
  spacing,
  sharedItemName,
  shareOrigin,
  className,
  onCopied,
  onShareIntent,
}) => {
  const intl = useIntl();
  const shareIntentProps = onShareIntent ? { onShareIntent } : {};
  return (
    <Stack spacing={spacing} className={className}>
      <Grid2 container rowSpacing={3} columnSpacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
        {/* First row includes both copy buttons */}
        <Grid2 size={{ xs: 2, sm: 4, md: 6 }}>
          <CopyTextButton
            text={text}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
        <Grid2 size={{ xs: 2, sm: 4, md: 6 }}>
          <CopyLinkButton
            url={url}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
      </Grid2>

      {/* Share on heading with horizontal lines */}
      <div className={css.shareHeading}>
        <div className={css.line}></div>
        <span className={css.shareText}>
          {intl.formatMessage({ id: 'ShareActions.shareOn', defaultMessage: 'Share on' })}
        </span>
        <div className={css.line}></div>
      </div>

      {/* Social media sharing buttons Grid2 */}
      <Grid2 container rowSpacing={3} columnSpacing={3} columns={{ xs: 4, sm: 8, md: 12 }}>
        {/* Rest of the buttons follow */}
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <WhatsAppButton
            url={url}
            title={title}
            text={text}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <TelegramButton
            url={url}
            title={title}
            text={text}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <MessengerButton
            url={url}
            text={text}
            sharedItemName={sharedItemName}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>

        {/* Second row on desktop (or split across rows on mobile) */}
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <SMSButton
            url={url}
            text={text}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <FacebookButton
            url={url}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
        <Grid2 size={{ xs: 2, sm: 4, md: 4 }}>
          <EmailButton
            url={url}
            title={title}
            text={text}
            sharedItemName={sharedItemName}
            shareOrigin={shareOrigin}
            fullWidth
            {...shareIntentProps}
          />
        </Grid2>
      </Grid2>

      {/* Removed standalone Copy Link button at the bottom */}
    </Stack>
  );
};

ShareButtonGroup.propTypes = {
  url: PropTypes.string,
  title: PropTypes.string,
  text: PropTypes.string,
  spacing: PropTypes.number,
  className: PropTypes.string,
  onCopied: PropTypes.func,
  onShareIntent: PropTypes.func,
  shareOrigin: PropTypes.string.isRequired,
  sharedItemName: oneOf(SHARED_ITEM_NAMES).isRequired,
};

ShareButtonGroup.defaultProps = {
  url: '',
  title: 'Check this out',
  text: '',
  spacing: 3,
  onCopied: () => {},
  onShareIntent: null,
};
